import { useEffect, useState, useSyncExternalStore } from "react";
import { Plane, MapPin, Timer, Map as MapIcon, RefreshCw } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import FlightMapModal from "./FlightMapModal";

/** Live-uppdaterande klocka (var 5:e sek) som triggar re-render via en
 * extern store — kompatibelt med React 19:s purity-regler. */
const tickListeners = new Set();
let tickTimer = null;
function ensureTickTimer() {
  if (tickTimer != null) return;
  tickTimer = setInterval(() => {
    tickListeners.forEach((cb) => cb());
  }, 5_000);
}
function subscribeToTick(cb) {
  ensureTickTimer();
  tickListeners.add(cb);
  return () => {
    tickListeners.delete(cb);
    if (tickListeners.size === 0 && tickTimer != null) {
      clearInterval(tickTimer);
      tickTimer = null;
    }
  };
}
function getTimeSnapshot() {
  return Math.floor(Date.now() / 5_000) * 5_000;
}
function useTickingNow() {
  return useSyncExternalStore(subscribeToTick, getTimeSnapshot, getTimeSnapshot);
}

/**
 * Inline expanderbar detalj-panel som visas under en FlightRow på Dashboard.
 * Innehåller: status-kort (gate/terminal/tid), animerad timeline (när planet
 * är i luften), WaitTime-kort (filtrerat på flygets terminal).
 *
 * Får hela `watch`-objektet som prop — parent (FlightsCard) pollar redan
 * `/flights/watched` var 90s så vi slipper duplicerade anrop.
 */
/**
 * Härled aktuell status från live ADS-B-data när Swedavia inte
 * hunnit uppdatera ännu. Returnerar {text, color, code} eller null om Swedavia
 * redan har en final-status (LAN/ARR/CNL) eller live-data saknas.
 */
export function deriveLiveStatus(s, watch) {
  if (!s) return null;
  const code = (s.status || "").toUpperCase();
  // Slutgiltig: landad. Sticky — visas även utan live_position.
  // VIKTIGT: denna check MÅSTE komma INNAN return null för LAN/ARR-koder,
  // annars faller UI tillbaka på gammal aero_status_text ("EnRoute") och
  // visar "I luften" trots att planet landat.
  if (s.actual_landing_utc || ["LAN", "ARR"].includes(code)) {
    return { text: "Landat", color: "#059669", code: "LAN" };
  }
  // Lita på Swedavia om de redan rapporterar slutgiltig avbokning
  if (["CNL", "CAN"].includes(code)) return null;
  const isArrival = watch?.kind === "arrival";
  const where = isArrival
    ? (watch?.airport_iata || "destinationen")
    : (s.destination || s.destination_name || "destinationen");
  const pos = s.live_position;
  // Live ADS-B-baserad inflygning/stig — kräver färska positionsdata
  if (pos && pos.on_ground !== true) {
    const alt = pos.altitude_m;
    const vs = pos.vertical_rate_ms;
    if (alt != null && alt < 4000 && vs != null && vs < -1) {
      return { text: `Slutlig inflygning mot ${where}`, color: "#D97706", code: "APP" };
    }
    if (vs != null && vs < -2) {
      return { text: `Nedstigning mot ${where}`, color: "#D97706", code: "DSC" };
    }
    if (vs != null && vs > 2) {
      return { text: "Stiger", color: "#059669", code: "CLB" };
    }
    if (alt != null && alt > 1000) {
      return { text: `I luften — mot ${where}`, color: "#059669", code: "FLY" };
    }
  }
  // Sticky-fallback: planet HAR lyft men live-position saknas just nu
  // (ADS-B-glapp, signal-tapp). Visa "I luften" istället för att låta
  // aero_status_text-rester (Boarding/FinalCall) lysa upp.
  const aeroNorm = String(s.aero_status_text || "").toLowerCase().replace(/[\s_-]/g, "");
  const swCode = String(s.status || "").toUpperCase();
  const inAirSignal =
    !!s.actual_takeoff_utc
    || ["enroute", "departed"].includes(aeroNorm)
    || ["ACT", "FLT", "DEP"].includes(swCode);
  if (inAirSignal) {
    return { text: `I luften — mot ${where}`, color: "#059669", code: "FLY" };
  }
  return null;
}

export function FlightDetailsPanel({ watch }) {
  const [waittime, setWaittime] = useState(null);
  const [enriching, setEnriching] = useState(false);

  const handleEnrich = async () => {
    if (!watch?.id || enriching) return;
    setEnriching(true);
    try {
      const { data } = await api.post(`/flights/watched/${watch.id}/enrich`);
      if (!data?.ok) {
        toast.info(data?.message || "Ingen ny data tillgänglig just nu");
      } else {
        const total = (data.new_fields?.length || 0) + (data.overwritten_fields?.length || 0);
        if (total === 0) {
          toast.info("All data var redan uppdaterad");
        } else {
          const labels = [...(data.new_fields || []), ...(data.overwritten_fields || [])]
            .map((f) => f.replace(/_/g, " "))
            .slice(0, 4)
            .join(", ");
          toast.success(`Berikad från ${data.source} — ${total} fält: ${labels}${total > 4 ? "…" : ""}`);
        }
        try {
          const mod = await import("./FlightsCard");
          await mod.refreshFlights?.();
        } catch { /* tyst */ }
        try {
          const mod = await import("./TripsCard");
          await mod.refreshTrips?.();
        } catch { /* tyst */ }
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Kunde inte berika just nu");
    } finally {
      setEnriching(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    async function loadWt() {
      if (watch?.kind !== "departure" || !watch.airport_iata) return;
      try {
        const wt = await api.get(`/flights/waittime/${watch.airport_iata}`);
        if (!cancelled) setWaittime(wt.data);
      } catch {
        if (!cancelled) setWaittime(null);
      }
    }
    loadWt();
    const t = setInterval(loadWt, 120_000); // var 2 min
    return () => { cancelled = true; clearInterval(t); };
  }, [watch?.id, watch?.kind, watch?.airport_iata]);

  if (!watch) return null;

  const s = watch.last_status || {};
  const sched = formatTime(s.scheduled_time);
  // För departures: visa actual_takeoff_utc (ADS-B-derived) som "verklig
  // tid" — annars faller vi tillbaka på Swedavias actual_time som ofta är
  // landings-tid när flighten landat (förstör avgångstidens visning).
  // För arrivals: actual_landing_utc → actual_time fallback.
  const isArr = watch?.kind === "arrival";
  const _actualSource = isArr
    ? (s.actual_landing_utc || s.actual_time)
    : (s.actual_takeoff_utc
        || (s.actual_time && s.actual_landing_utc
            && s.actual_time === s.actual_landing_utc
              ? null : s.actual_time));
  const actual = formatTime(_actualSource);
  // estimated_time = Swedavias "estimatedUtc" (t.ex. "Ankommer 16:22"
  // som tavlan visar). Prioriteras över sched men efter actual.
  const estimated = formatTime(s.estimated_time);
  const displayTime = actual || estimated || sched;
  // Härled live-status: ADS-B → AeroDataBox webhook → Swedavia
  const live = deriveLiveStatus(s, watch);
  const _aeroNorm = String(s.aero_status_text || "").toLowerCase().replace(/[\s_-]/g, "");
  const _aeroSvMap = {
    boarding: "Boarding", finalcall: "Final call", gateclosed: "Gate stängd",
    departed: "Avgått", enroute: "I luften", diverted: "Omdirigerad",
    cancelled: "Inställd", canceled: "Inställd", landed: "Landad",
    arrived: "Landad", delayed: "Försenad", checkin: "Incheckning",
  };
  // Filtrera bort stale gate-fas-status när vi vet att planet passerat den.
  const _gatePhase = ["checkin", "boarding", "finalcall", "gateclosed"].includes(_aeroNorm);
  const _swCodeUp = String(s.status || "").toUpperCase();
  const _passedGatePhase =
    !!s.actual_takeoff_utc
    || !!s.actual_landing_utc
    || s.taxiing_seen
    || ["enroute", "departed", "landed", "arrived", "diverted"].includes(_aeroNorm)
    || ["ACT", "FLT", "LAN", "ARR", "DEP"].includes(_swCodeUp)
    || ((s.live_position?.altitude_m || 0) >= 300 && !s.live_position?.on_ground);
  const _aeroValid = _aeroNorm
    && _aeroNorm !== "expected"
    && _aeroNorm !== "unknown"
    && !(_gatePhase && _passedGatePhase);
  const aeroSv = _aeroValid
    ? (_aeroSvMap[_aeroNorm] || s.aero_status_text)
    : "";
  const statusText = live?.text || aeroSv || s.remark_swedish || s.remark_english || "";
  const statusCode = (live?.code || s.status || "").toUpperCase();
  const isAirborne = ["ACT", "DEP", "GOD", "ENR", "FLY", "CLB", "DSC", "APP"].includes(statusCode);
  const isLanded = ["LAN", "ARR"].includes(statusCode);
  const isCancelled = ["CNL", "CAN", "DEL"].includes(statusCode);
  const isDelayed = ["SEQ", "DLY", "EXP", "NXT"].includes(statusCode);
  // gateAction (B=Boarding, G=Go to gate, F=Final call, C=Gate closed) — har
  // egen färg så användaren ser direkt att det är dags att röra på sig.
  const gateAction = (s.gate_action || "").toUpperCase();
  const isBoarding = ["B", "G", "F"].includes(gateAction);
  const isGateClosed = gateAction === "C";

  const tone = live ? live.color
    : isCancelled || isGateClosed ? "#DC2626"
    : isBoarding ? "#7C3AED"             // lila — aktiv boarding/gate
    : isDelayed ? "#D97706"
    : isAirborne || isLanded ? "#059669"
    : "var(--text-primary)";

  const flightTerminal = s.terminal || watch.terminal;

  return (
    <div
      className="mt-2 ml-4 pl-3 border-l-2 border-[color:var(--border-subtle)] space-y-2"
      data-testid={`flight-details-${watch.flight_id}`}
    >
      {/* Manuell berikningsknapp — kallar ADB just nu och fyller i saknad
          origin-data, takeoff-tid m.m. Använd när Swedavia/webhook missat. */}
      <div className="flex justify-end -mt-1 pt-1">
        <button
          type="button"
          onClick={handleEnrich}
          disabled={enriching}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-[color:var(--accent-soft,rgba(99,102,241,0.12))] text-[color:var(--accent-color,#6366f1)] hover:bg-[color:var(--accent-color,#6366f1)] hover:text-white border border-[color:var(--accent-color,#6366f1)]/30 transition disabled:opacity-50"
          data-testid={`flight-enrich-${watch.flight_id}`}
          title="Hämta uppdaterad data från AeroDataBox (terminal, gate, takeoff-tid)"
        >
          <RefreshCw size={13} className={enriching ? "animate-spin" : ""} />
          {enriching ? "Berikar…" : "Berika nu"}
        </button>
      </div>

      {/* Status + tider */}
      {/* Status-kortet (tid + gate + terminal + destination) flyttat
          till master-raden ovanför för att slippa dubbel-presentation.
          Här fortsätter vi direkt med berikande datakort. */}

      {/* Live ADS-B tracking — höjd, fart, taxning, verklig takeoff (adsb.lol/OpenSky)
          Göms när flyget har landat — live-position är inte längre relevant. */}
      {!isLanded && (s.live_position || s.actual_takeoff_utc || s.taxiing_seen) && (
        <LiveTrackingCard s={s} />
      )}

      {/* Flygplansinformation — registrering, ICAO24, callsign, ursprungsland */}
      {(s.aircraft_registration || s.callsign || s.icao24 || s.live_position?.callsign ||
        s.airline || s.origin_name || s.destination_name || s.di_indicator) && (
        <AircraftInfoCard s={s} />
      )}

      {/* Resemål: ankomstinfo vid destinationen + utländsk avgångsinfo
          för arrival-watches (hemresa). Göms när landat — UTOM bagageband
          vid ankomst (det är då man behöver det). */}
      {(!isLanded && (s.baggage_claim || s.gate_open_utc ||
        s.arrival_terminal || s.arrival_gate || s.aero_arrival_terminal ||
        s.aero_arrival_gate || s.arrival_baggage_belt || s.arrival_runway ||
        s.arrival_scheduled_local || s.arrival_actual_local ||
        s.origin_terminal || s.origin_gate || s.destination_name ||
        (s.codeshares && s.codeshares.length > 0) ||
        (s.via_destinations && s.via_destinations.length > 0))) && (
        <RouteExtrasCard s={s} watch={watch} landed={false} />
      )}
      {isLanded && watch.kind === "arrival" && (s.baggage_claim || s.arrival_baggage_belt) && (
        <RouteExtrasCard s={s} watch={watch} landed={true} />
      )}

      {/* Flight progress timeline (visas när flighten är i luften, har live data eller landad) */}
      {(isAirborne || isLanded || s.live_position) && (
        <FlightTimeline status={s} watch={watch} landed={isLanded} />
      )}

      {/* WaitTime (security checkpoint) — filtrerat på flygets terminal.
          Göms när landat — säkerhetskön är irrelevant efter landning. */}
      {watch.kind === "departure" && !isLanded && waittime && (
        <WaitTimeCard data={waittime} airport={watch.airport_iata} terminal={flightTerminal} />
      )}

      {/* Händelse-logg — alla pushar/ändringar som skickats för detta flyg */}
      {Array.isArray(watch.change_log) && watch.change_log.length > 0 && (
        <ChangeLogCard entries={watch.change_log} />
      )}

      {/* Geek mode — visa all rå ADS-B-telemetri från adsb.lol */}
      {s.live_position?.raw_telemetry && (
        <GeekModeCard tel={s.live_position.raw_telemetry} s={s.live_position} />
      )}

      {/* Källor-fotnot */}
      <SourcesFooter s={s} />
    </div>
  );
}

function SourcesFooter({ s }) {
  const swedavia = s.data_source === "swedavia" || !s.data_source;
  const livePos = s.live_position || {};
  const adsblol = livePos.source === "adsblol";
  const opensky = !!livePos.altitude_m && !adsblol;
  const aerodatabox = !!s.aero_updated_at || s.enrichment_source === "aerodatabox";
  const airlabs = !!s.airlabs_data;
  // Lista bara källor som FAKTISKT har gett data till denna flight
  const active = [
    swedavia && "Swedavia",
    aerodatabox && "AeroDataBox",
    adsblol && "adsb.lol",
    opensky && "OpenSky",
    airlabs && "AirLabs",
  ].filter(Boolean);
  if (active.length === 0) return null;
  return (
    <div className="text-[9px] text-[color:var(--text-tertiary)] flex items-center gap-2 px-1 flex-wrap"
         data-testid="flight-sources">
      <span>Källor:</span>
      {active.map((label) => (
        <SourceBadge key={label} active={true} label={label} />
      ))}
    </div>
  );
}

function SourceBadge({ active, label }) {
  return (
    <span className={`px-1.5 py-0.5 rounded ${
      active
        ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
        : "bg-zinc-100 text-zinc-400"
    }`}>
      {active ? "●" : "○"} {label}
    </span>
  );
}


function GeekModeCard({ tel, s }) {
  const [open, setOpen] = useState(false);

  // Formatters
  const km_h = (kn) => (kn != null ? `${Math.round(kn * 1.852)} km/h` : "—");
  const kn = (v) => (v != null ? `${Math.round(v)} kn` : "—");
  const ft = (v) => (v != null ? `${Math.round(v).toLocaleString("sv-SE")} ft` : "—");
  const deg = (v) => (v != null ? `${Math.round(v)}°` : "—");
  const num = (v, d = 2) => (v != null ? Number(v).toFixed(d) : "—");
  const c = (v) => (v != null ? `${Math.round(v)} °C` : "—");

  // Squawk-koder med specialbetydelser
  const squawkLabel = (sq) => {
    if (!sq) return "—";
    if (sq === "7500") return `${sq} ⚠️ Kapning`;
    if (sq === "7600") return `${sq} ⚠️ Radioförlust`;
    if (sq === "7700") return `${sq} 🆘 Nödläge`;
    if (sq === "1000") return `${sq} (standard)`;
    return sq;
  };

  // Vector från true track till mag heading → drift-vinkel (om båda finns)
  const drift = (tel.true_heading != null && s.heading != null)
    ? (s.heading - tel.true_heading).toFixed(1)
    : null;

  return (
    <div className="card-soft" data-testid="flight-geek-mode">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-3 py-2 text-sm font-semibold text-[color:var(--text-secondary)]"
        data-testid="flight-geek-mode-toggle"
      >
        <span>🤓 Geek mode — rå ADS-B-telemetri</span>
        <span className={`text-xs transition-transform ${open ? "rotate-180" : ""}`}>▾</span>
      </button>

      {open && (
        <div className="px-3 pb-3 space-y-3 text-xs">
          {/* 1. Identifikation */}
          <Section title="Identifikation">
            <GR label="ICAO24" value={s.icao24 || "—"} mono />
            <GR label="Callsign" value={s.callsign || "—"} mono />
            <GR label="Registrering" value={tel.registration || "—"} mono />
            <GR label="Typ" value={tel.aircraft_type || "—"} mono />
            <GR label="Wake-kategori" value={tel.category || "—"} />
            <GR label="Squawk" value={squawkLabel(tel.squawk)} mono />
          </Section>

          {/* 2. Hastigheter */}
          <Section title="Hastigheter">
            <GR label="Mark (GS)" value={km_h((s.speed_ms || 0) * 1.94384)} />
            <GR label="Indikerad (IAS)" value={km_h(tel.ias_kn) + ` (${kn(tel.ias_kn)})`} />
            <GR label="Sann (TAS)" value={km_h(tel.tas_kn) + ` (${kn(tel.tas_kn)})`} />
            <GR label="Mach" value={tel.mach != null ? `M ${num(tel.mach, 3)}` : "—"} />
          </Section>

          {/* 3. Bäring & rörelse */}
          <Section title="Bäring & rörelse">
            <GR label="Track (GPS)" value={deg(s.heading)} />
            <GR label="Magnetisk" value={deg(tel.mag_heading)} />
            <GR label="Sann" value={deg(tel.true_heading)} />
            {drift !== null && <GR label="Drift" value={`${drift}°`} />}
            <GR label="Roll" value={deg(tel.roll)} />
            <GR label="Sväng-rate" value={tel.track_rate != null ? `${num(tel.track_rate, 2)}°/s` : "—"} />
            <GR label="Stignings-rate" value={tel.baro_rate_ftpm != null ? `${tel.baro_rate_ftpm} ft/min` : "—"} />
          </Section>

          {/* 4. Atmosfär */}
          <Section title="Atmosfär">
            <GR label="Höjd (baro)" value={ft(tel.alt_baro_ft)} />
            <GR label="Höjd (geom)" value={ft(tel.alt_geom_ft)} />
            <GR label="OAT (utomhus)" value={c(tel.oat_c)} />
            <GR label="TAT (totalt)" value={c(tel.tat_c)} />
            <GR label="QNH" value={tel.nav_qnh != null ? `${tel.nav_qnh} hPa` : "—"} />
            <GR label="Vind"
                value={
                  tel.wind_dir != null && tel.wind_speed_kn != null
                    ? `${deg(tel.wind_dir)} @ ${km_h(tel.wind_speed_kn)}`
                    : "—"
                } />
          </Section>

          {/* 5. Autopilot / FMS */}
          {(tel.nav_altitude_mcp_ft != null || tel.nav_altitude_fms_ft != null
            || tel.nav_heading != null) && (
            <Section title="Autopilot / FMS">
              <GR label="Vald höjd (MCP)" value={ft(tel.nav_altitude_mcp_ft)} />
              <GR label="FMS-höjd" value={ft(tel.nav_altitude_fms_ft)} />
              <GR label="Vald bäring" value={deg(tel.nav_heading)} />
            </Section>
          )}

          {/* 6. Position */}
          <Section title="Position">
            <GR label="Latitud" value={s.latitude != null ? num(s.latitude, 4) + "°" : "—"} mono />
            <GR label="Longitud" value={s.longitude != null ? num(s.longitude, 4) + "°" : "—"} mono />
          </Section>

          {/* 7. ADS-B-mottagning */}
          <Section title="ADS-B-mottagning">
            <GR label="Meddelanden" value={tel.messages != null ? tel.messages.toLocaleString("sv-SE") : "—"} />
            <GR label="Senast sedd" value={tel.seen_s != null ? `${num(tel.seen_s, 1)} s sedan` : "—"} />
            <GR label="Senaste position" value={tel.seen_pos_s != null ? `${num(tel.seen_pos_s, 1)} s sedan` : "—"} />
          </Section>
        </div>
      )}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider font-bold text-[color:var(--text-tertiary)] mb-1">
        {title}
      </div>
      <div className="grid grid-cols-2 gap-x-3 gap-y-1">{children}</div>
    </div>
  );
}

function GR({ label, value, mono }) {
  return (
    <div className="flex justify-between items-baseline gap-2 leading-tight">
      <span className="text-[color:var(--text-tertiary)] text-[11px]">{label}</span>
      <span className={`text-right text-[12px] ${mono ? "font-mono" : ""}`}>{value}</span>
    </div>
  );
}


function ChangeLogCard({ entries }) {
  // Visa senaste först
  const sorted = [...entries].reverse();
  return (
    <div className="card-soft p-3" data-testid="flight-change-log">
      <p className="text-[10px] font-semibold uppercase tracking-wider text-[color:var(--text-tertiary)] mb-2">
        Händelser ({sorted.length})
      </p>
      <ul className="space-y-1.5">
        {sorted.slice(0, 15).map((e, i) => {
          const t = formatLogTime(e.timestamp);
          return (
            <li key={i} className="flex items-start gap-2 text-xs">
              <span className="text-[10px] text-[color:var(--text-tertiary)] tabular-nums shrink-0 mt-0.5 w-10">
                {t}
              </span>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-[color:var(--text-primary)] leading-tight">{e.title}</p>
                <p className="text-[11px] text-[color:var(--text-secondary)] leading-tight">{e.body}</p>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function AircraftInfoCard({ s }) {
  const pos = s.live_position || {};
  const reg = s.aircraft_registration;
  const icao24 = s.icao24 || pos.icao24;
  const callsign = s.callsign || pos.callsign;
  const country = pos.origin_country;
  const airline = s.airline;
  const diMap = { D: "Inrikes", I: "Utrikes", S: "Schengen" };
  const diLabel = diMap[s.di_indicator];
  const distanceKm = s.great_circle_distance_km;
  const distanceStr = (typeof distanceKm === "number" && distanceKm > 0)
    ? `${Math.round(distanceKm).toLocaleString("sv-SE")} km`
    : null;
  // Bana: visa både avgång och ankomst om båda finns
  let runwayStr = null;
  if (s.departure_runway && s.arrival_runway) {
    runwayStr = `${s.departure_runway} → ${s.arrival_runway}`;
  } else if (s.departure_runway) {
    runwayStr = `${s.departure_runway} (avgång)`;
  } else if (s.arrival_runway) {
    runwayStr = `${s.arrival_runway} (ankomst)`;
  }
  const takeoffLocal = s.actual_takeoff_utc
    ? new Date(_normalizeIso(s.actual_takeoff_utc)).toLocaleTimeString("sv-SE", {
        hour: "2-digit", minute: "2-digit", timeZone: "Europe/Stockholm",
      })
    : null;
  const wheelsUpLocal = s.wheels_up_utc
    ? new Date(_normalizeIso(s.wheels_up_utc)).toLocaleTimeString("sv-SE", {
        hour: "2-digit", minute: "2-digit", timeZone: "Europe/Stockholm",
      })
    : null;
  const landingLocal = s.actual_landing_utc
    ? new Date(_normalizeIso(s.actual_landing_utc)).toLocaleTimeString("sv-SE", {
        hour: "2-digit", minute: "2-digit", timeZone: "Europe/Stockholm",
      })
    : null;
  return (
    <div className="card-soft p-3" data-testid="flight-aircraft-info">
      <p className="text-[10px] font-semibold uppercase tracking-wider text-[color:var(--text-tertiary)] mb-2">
        Flygplansinformation
      </p>
      <div className="grid grid-cols-2 gap-2 text-xs">
        {airline && <InfoRow label="Flygbolag" value={airline} />}
        {diLabel && <InfoRow label="Trafiktyp" value={diLabel} />}
        {s.aircraft_model && <InfoRow label="Flygplansmodell" value={s.aircraft_model} />}
        {distanceStr && <InfoRow label="Sträcka" value={distanceStr} />}
        {runwayStr && <InfoRow label="Bana" value={runwayStr} />}
        {takeoffLocal && <InfoRow label="Off-block (gate)" value={takeoffLocal} />}
        {wheelsUpLocal && <InfoRow label="Startat (wheels-up)" value={wheelsUpLocal} />}
        {landingLocal && <InfoRow label="Verklig landning" value={landingLocal} />}
        {s.aero_status_text && <InfoRow label="Status (AeroDataBox)" value={s.aero_status_text} />}
        {reg && <InfoRow label="Registrering" value={reg} mono />}
        {icao24 && <InfoRow label="ICAO24" value={icao24.toLowerCase()} mono />}
        {callsign && <InfoRow label="Callsign" value={callsign} mono />}
        {country && <InfoRow label="Registrerad i" value={country} />}
        {s.origin_name && <InfoRow label="Avgång från" value={`${s.origin_name} (${s.origin || "?"})`} />}
      </div>
    </div>
  );
}

function RouteExtrasCard({ s, watch, landed }) {
  const isArrival = watch?.kind === "arrival";

  // ─── Avgångssidan ─────────────────────────────────────────────────────
  // departure-watch: origin = ARN (Swedavia s.terminal/s.gate), så vi
  // exponerar s.terminal/s.gate/s.checkin_desks. arrival-watch: origin =
  // utländsk avgångsort, så vi använder s.origin_*.
  const depAirport = isArrival
    ? (s.origin_name && (s.origin ? `${s.origin_name} (${s.origin})` : s.origin_name))
    : (watch?.airport_iata
        ? `Stockholm-Arlanda (${watch.airport_iata})`
        : (s.origin_name || null));
  const depTerm = isArrival ? s.origin_terminal : s.terminal;
  const depGate = isArrival ? s.origin_gate : s.gate;
  const depCheckin = isArrival ? s.origin_checkin : s.checkin_desks;
  // Avgångstider: för departure-watch är scheduled/actual_time från
  // Swedavia på origin-sidan. För arrival-watch kommer den från
  // departure_scheduled/departure_actual i AeroDataBox-berikningen.
  // KRITISKT: för departures används `actual_takeoff_utc` (ADS-B-derived
  // wheels-up) FÖRE `actual_time`. Swedavia sätter ofta `actual_time` till
  // Tider är redan canonical-skyddade (backend `_safe_dep_iso` filtrerar
  // bort poisoned-actuals där dep == land). Vi läser därför rakt.
  const depSchedIso = isArrival ? s.departure_scheduled : s.scheduled_time;
  const depActIso = isArrival
    ? s.departure_actual
    : (s.actual_takeoff_utc
        || s.departure_actual
        || s.actual_time);
  const depTz = s.origin_timezone;
  const depSchedLocal = formatLocalTime(
    isArrival ? s.departure_scheduled_local : null,
    depSchedIso, depTz,
  );
  const depActLocal = formatLocalTime(
    isArrival ? s.departure_actual_local : null,
    depActIso, depTz,
  );
  const gateOpenLocal = s.gate_open_utc
    ? new Date(_normalizeIso(s.gate_open_utc)).toLocaleTimeString("sv-SE", {
        hour: "2-digit", minute: "2-digit", timeZone: depTz || "Europe/Stockholm",
      })
    : null;

  // ─── Ankomstsidan ─────────────────────────────────────────────────────
  const destDisplay = s.destination_name && (s.destination
    ? `${s.destination_name} (${s.destination})`
    : s.destination_name);
  const arrAirport = isArrival
    ? (watch?.airport_iata ? `Stockholm-Arlanda (${watch.airport_iata})` : "Stockholm-Arlanda (ARN)")
    : destDisplay;
  // Swedavia + AeroDataBox-merge för ankomstterminal/gate. För
  // arrival-watch är ARN-ankomstdatat i s.terminal/s.gate (Swedavia) eller
  // s.arrival_terminal (ADB) — båda är ankomstssidan.
  const arrTerm = isArrival
    ? (s.arrival_terminal || s.aero_arrival_terminal || s.terminal)
    : (s.arrival_terminal || s.aero_arrival_terminal);
  const arrGate = isArrival
    ? (s.arrival_gate || s.aero_arrival_gate || s.gate)
    : (s.arrival_gate || s.aero_arrival_gate);
  const baggage = s.baggage_claim || s.arrival_baggage_belt;
  // Ankomsttider: för arrival-watch är scheduled/actual_time från Swedavia
  // ankomstsida. För departure-watch kommer det från arrival_scheduled/
  // arrival_actual i AeroDataBox-berikningen. Prioritera ADS-B-derived
  // `actual_landing_utc` om finns (mest exakt touchdown-tid).
  const arrSchedIso = isArrival ? s.scheduled_time : s.arrival_scheduled;
  const arrActIso = isArrival
    ? (s.actual_landing_utc || s.actual_time)
    : (s.actual_landing_utc || s.arrival_actual);
  const arrTz = s.destination_timezone;
  const arrSchedLocal = formatLocalTime(
    isArrival ? null : s.arrival_scheduled_local,
    arrSchedIso, arrTz,
  );
  const arrActLocal = formatLocalTime(
    isArrival ? null : s.arrival_actual_local,
    arrActIso, arrTz,
  );

  // ─── Landed-läget (Bagage-only) ───────────────────────────────────────
  if (landed) {
    if (!baggage) return null;
    return (
      <div className="card-soft p-3" data-testid="flight-route-extras">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-[color:var(--text-tertiary)] mb-2">
          Bagage
        </p>
        <div className="text-xs">
          <InfoRow label="Bagageband" value={baggage} />
        </div>
      </div>
    );
  }

  // ─── Hjälp-funktion: render en sektion ────────────────────────────────
  // (definierad nedan som RouteSection — flyttad utanför för att undvika
  // remount vid re-render)

  const depRows = [
    depAirport && { label: "Flygplats", value: depAirport },
    (depSchedLocal || depActLocal) && {
      type: "time", label: "Avgångstid",
      schedIso: depSchedIso, actIso: depActIso,
      schedLocal: depSchedLocal, actLocal: depActLocal,
    },
    depTerm && { label: "Terminal", value: depTerm },
    depGate && { label: "Gate", value: depGate },
    depCheckin && { label: "Incheckning", value: depCheckin },
    gateOpenLocal && { label: "Gate öppnar", value: gateOpenLocal },
    s.departure_runway && { label: "Bana", value: s.departure_runway },
  ];
  const arrRows = [
    arrAirport && { label: "Flygplats", value: arrAirport },
    (arrSchedLocal || arrActLocal) && {
      type: "time", label: "Ankomsttid",
      schedIso: arrSchedIso, actIso: arrActIso,
      schedLocal: arrSchedLocal, actLocal: arrActLocal,
    },
    arrTerm && { label: "Terminal", value: arrTerm },
    arrGate && { label: "Gate", value: arrGate },
    baggage && { label: "Bagageband", value: baggage },
    s.arrival_runway && { label: "Bana", value: s.arrival_runway },
  ];
  const extrasRows = [
    s.via_destinations?.length > 0 && { label: "Via", value: s.via_destinations.join(" → ") },
    s.codeshares?.length > 0 && { label: "Codeshare", value: s.codeshares.slice(0, 5).join(", ") },
  ];

  // Räkna rader baserat på antingen value (statisk) eller schedLocal/actLocal (TimeRow)
  const hasRow = (r) => r && (r.value || r.schedLocal || r.actLocal);
  const hasDep = depRows.some(hasRow);
  const hasArr = arrRows.some(hasRow);
  const hasExtras = extrasRows.some(hasRow);
  if (!hasDep && !hasArr && !hasExtras) return null;

  return (
    <div className="card-soft p-3 space-y-3" data-testid="flight-route-extras">
      <RouteSection title="Avgående" emoji="🛫" color="#7C3AED" rows={depRows} />
      {hasDep && hasArr && (
        <div className="border-t border-[color:var(--border-secondary,#e5e7eb)]" />
      )}
      <RouteSection title="Ankomst" emoji="🛬" color="#0891B2" rows={arrRows} />
      {hasExtras && (
        <>
          <div className="border-t border-[color:var(--border-secondary,#e5e7eb)]" />
          <RouteSection title="Övrigt" emoji="ℹ️" color="#6B7280" rows={extrasRows} />
        </>
      )}
    </div>
  );
}

function RouteSection({ title, emoji, color, rows }) {
  const visibleRows = rows.filter((r) => r && (r.value || r.schedLocal || r.actLocal));
  if (visibleRows.length === 0) return null;
  return (
    <div
      className="space-y-1.5"
      data-testid={`route-${title.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <div className="flex items-center gap-1.5">
        <span className="text-xs">{emoji}</span>
        <p
          className="text-[10px] font-semibold uppercase tracking-wider"
          style={{ color }}
        >
          {title}
        </p>
      </div>
      <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 pl-5">
        {visibleRows.map((r, i) =>
          r.type === "time" ? (
            <TimeRow
              key={i}
              label={r.label}
              schedIso={r.schedIso}
              actIso={r.actIso}
              schedLocal={r.schedLocal}
              actLocal={r.actLocal}
            />
          ) : (
            <InfoRow key={i} label={r.label} value={r.value} mono={r.mono} />
          )
        )}
      </div>
    </div>
  );
}

function formatLocalTime(localIso, utcIso, tz) {
  // Returnerar "08:25 (Europe/Madrid)" eller "08:25" om tz saknas
  try {
    if (localIso) {
      // AeroDataBox local ISO är typ "2026-06-12 12:10+02:00" eller "2026-06-12T12:10"
      // Vi vill bara visa HH:MM utan att tolka som UTC
      const m = /(\d{2}):(\d{2})/.exec(localIso);
      if (m) {
        const t = `${m[1]}:${m[2]}`;
        return tz ? `${t} (${tz})` : t;
      }
    }
    if (utcIso && tz) {
      const t = new Date(_normalizeIso(utcIso)).toLocaleTimeString("sv-SE", {
        hour: "2-digit", minute: "2-digit", timeZone: tz,
      });
      return `${t} (${tz})`;
    }
  } catch { /* ignore */ }
  return null;
}

function InfoRow({ label, value, mono }) {
  return (
    <div className="flex flex-col">
      <span className="text-[9px] uppercase tracking-wider text-[color:var(--text-tertiary)]">{label}</span>
      <span className={`text-sm ${mono ? "font-mono" : ""} text-[color:var(--text-primary)]`}>
        {value}
      </span>
    </div>
  );
}

/** Beräkna delta i minuter mellan schemalagd och faktisk tid. Returnerar
 *  ett objekt { mins, label, color } eller null om båda saknas eller diffen
 *  är < 1 min (i tid). */
export function computeDelay(schedIso, actIso) {
  if (!schedIso || !actIso) return null;
  try {
    const sched = new Date(_normalizeIso(schedIso)).getTime();
    const act = new Date(_normalizeIso(actIso)).getTime();
    const mins = Math.round((act - sched) / 60_000);
    if (Math.abs(mins) < 1) return { mins: 0, label: "i tid", color: "#059669" };
    if (mins > 0) return { mins, label: `+${mins} min`, color: "#DC2626" };
    return { mins, label: `−${Math.abs(mins)} min`, color: "#059669" };
  } catch {
    return null;
  }
}

/** Rendera en tids-rad med delay-badge: "08:37  +12 min  (sched. 08:25)" */
function TimeRow({ label, schedIso, actIso, schedLocal, actLocal }) {
  // Vi visar actual om finns, annars sched. Delay-badge bara om actual avviker.
  const primary = actLocal || schedLocal;
  if (!primary) return null;
  const delay = computeDelay(schedIso, actIso);
  const isLate = delay && delay.mins > 0;
  const isEarly = delay && delay.mins < 0;
  const showSchedRef = delay && (isLate || isEarly) && schedLocal && schedLocal !== primary;
  return (
    <div className="flex flex-col">
      <span className="text-[9px] uppercase tracking-wider text-[color:var(--text-tertiary)]">{label}</span>
      <span className="text-sm text-[color:var(--text-primary)] flex items-baseline flex-wrap gap-x-1.5">
        <span className="tabular-nums font-semibold">{primary}</span>
        {delay && (isLate || isEarly) && (
          <span
            className="text-[10px] font-semibold px-1 py-0.5 rounded tabular-nums"
            style={{
              color: delay.color,
              backgroundColor: isLate ? "#FEE2E2" : "#D1FAE5",
            }}
          >
            {delay.label}
          </span>
        )}
        {showSchedRef && (
          <span className="text-[10px] text-[color:var(--text-tertiary)] tabular-nums">
            (sched. {schedLocal})
          </span>
        )}
      </span>
    </div>
  );
}

function LiveTrackingCard({ s }) {
  // Tick var 5:e sek för "uppdaterad sedan"-räknare
  const now = useTickingNow();
  const pos = s.live_position || {};
  const altM = pos.altitude_m;
  const speedKmh = pos.speed_ms != null ? Math.round(pos.speed_ms * 3.6) : null;
  const vsMs = pos.vertical_rate_ms;
  const heading = pos.heading;
  const onGround = pos.on_ground === true;
  const takeoff = s.actual_takeoff_utc;
  const taxiing = s.taxiing_seen && !s.actual_takeoff_utc;

  // "Uppdaterad för X sek/min sedan" baserat på fetched_at
  let freshnessLabel = "";
  if (pos.fetched_at) {
    try {
      const ageSec = Math.max(0, Math.round((now - new Date(pos.fetched_at).getTime()) / 1000));
      if (ageSec < 60) freshnessLabel = `${ageSec}s sedan`;
      else if (ageSec < 3600) freshnessLabel = `${Math.round(ageSec / 60)} min sedan`;
      else freshnessLabel = `${Math.round(ageSec / 3600)}h sedan`;
    } catch { /* ignore */ }
  }

  let phaseLabel = "";
  let phaseColor = "#059669";
  if (taxiing && onGround) { phaseLabel = "Taxar mot startbanan"; phaseColor = "#7C3AED"; }
  else if (onGround && altM != null && altM < 100) { phaseLabel = "På marken"; phaseColor = "#7C3AED"; }
  else if (vsMs != null && vsMs > 2) { phaseLabel = "Stiger"; phaseColor = "#059669"; }
  else if (vsMs != null && vsMs < -2) { phaseLabel = "Sjunker — närmar sig landning"; phaseColor = "#D97706"; }
  else if (!onGround) { phaseLabel = "Kryssfart"; phaseColor = "#059669"; }

  // Källetikett — adsb.lol är primär, OpenSky är fallback
  const sourceLabel = s.source === "adsblol" ? "adsb.lol ADS-B" : "OpenSky ADS-B";

  return (
    <div className="card-soft p-3" data-testid="flight-live-tracking">
      <div className="flex items-center justify-between mb-2">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-[color:var(--text-tertiary)]">
          Live ({sourceLabel})
        </p>
        {phaseLabel && (
          <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded"
                style={{ background: phaseColor + "22", color: phaseColor }}>
            {phaseLabel}
          </span>
        )}
      </div>
      {takeoff && (
        <div className="mb-2 text-xs">
          <span className="font-semibold text-[color:var(--text-primary)]">Verklig start: </span>
          <span className="tabular-nums">{formatTime(takeoff)}</span>
          <DelayBadge actualUtc={takeoff} scheduledUtc={s.scheduled_time} />
        </div>
      )}
      <div className="grid grid-cols-3 gap-2">
        <LiveMetric label="Höjd" value={altM != null ? formatAlt(altM) : "—"} />
        <LiveMetric label="Fart" value={speedKmh != null ? `${speedKmh} km/h` : "—"} />
        <LiveMetric label="Kurs" value={heading != null ? `${Math.round(heading)}°` : "—"} />
      </div>
      {(pos.latitude != null && pos.longitude != null) && (
        <p className="text-[10px] text-[color:var(--text-tertiary)] mt-2 tabular-nums flex items-center justify-between">
          <span>{pos.latitude.toFixed(3)}°N, {pos.longitude.toFixed(3)}°E</span>
          {freshnessLabel && (
            <span className="flex items-center gap-1">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
              {freshnessLabel}
            </span>
          )}
        </p>
      )}
    </div>
  );
}

function LiveMetric({ label, value }) {
  return (
    <div className="text-center px-2 py-1 rounded-lg bg-[color:var(--bg-elevated)]">
      <p className="text-[9px] uppercase tracking-wider text-[color:var(--text-tertiary)]">{label}</p>
      <p className="text-sm font-bold tabular-nums">{value}</p>
    </div>
  );
}

function DelayBadge({ actualUtc, scheduledUtc }) {
  if (!actualUtc || !scheduledUtc) return null;
  const a = new Date(actualUtc).getTime();
  const s = new Date(scheduledUtc).getTime();
  if (isNaN(a) || isNaN(s)) return null;
  const min = Math.round((a - s) / 60000);
  if (Math.abs(min) <= 1) return null;
  const isLate = min > 0;
  return (
    <span className="ml-1 text-[10px] font-semibold px-1 py-0.5 rounded"
          style={{ background: isLate ? "#FEE2E2" : "#DCFCE7",
                   color: isLate ? "#B91C1C" : "#16A34A" }}>
      {isLate ? `+${min} min` : `${min} min`}
    </span>
  );
}

function formatAlt(m) {
  if (m == null) return "";
  if (m < 50) return "marknivå";
  const rounded = Math.round(m / 100) * 100;
  return `${rounded.toLocaleString("sv-SE")} m`;
}


function formatLogTime(iso) {
  if (!iso) return "";
  try {
    const d = new Date(_normalizeIso(iso));
    const today = new Date();
    if (d.toDateString() === today.toDateString()) {
      return d.toLocaleTimeString("sv-SE", { hour: "2-digit", minute: "2-digit" });
    }
    return d.toLocaleDateString("sv-SE", { day: "2-digit", month: "2-digit" });
  } catch { return ""; }
}

function Badge({ label, value }) {
  return (
    <div className="text-center px-2 py-1 rounded-lg bg-[color:var(--bg-elevated)]">
      <p className="text-[9px] uppercase tracking-wider text-[color:var(--text-tertiary)]">{label}</p>
      <p className="text-xs font-bold tabular-nums">{value}</p>
    </div>
  );
}

function normalizeTerminal(t) {
  if (!t) return "";
  return String(t).toUpperCase().replace(/^T/, "").trim();
}

function WaitTimeCard({ data, airport, terminal }) {
  const queues = data?.waitTimes || data?.queues || data?.waitTimeQueues || data?.WaitTimes || [];
  const allQueues = Array.isArray(queues) ? queues : [];
  // Filtrera på flygets terminal när det är känt — annars visa alla.
  const target = normalizeTerminal(terminal);
  const filtered = target
    ? allQueues.filter((q) => normalizeTerminal(q.terminal) === target)
    : allQueues;
  const queueList = filtered.length > 0 ? filtered : allQueues;
  const filteredBy = target && filtered.length > 0 ? ` · T${target}` : "";

  return (
    <div className="card-soft p-3" data-testid="flight-waittime">
      <p className="text-[10px] font-semibold uppercase tracking-wider text-[color:var(--text-tertiary)] mb-2 flex items-center gap-1">
        <Timer size={11} />
        Säkerhetskö {airport}{filteredBy}
      </p>
      {queueList.length === 0 ? (
        <p className="text-xs text-[color:var(--text-tertiary)]">Inga aktiva köer just nu.</p>
      ) : (
        <ul className="space-y-1">
          {queueList.slice(0, 8).map((q, i) => {
            const wait = q.currentProjectedWaitTime ?? q.waitTime ?? q.estimatedWaitMinutes ?? q.waitMinutes ?? q.minutesToWait;
            const name = q.queueName || q.checkpoint || q.name || `Kö ${i + 1}`;
            const isFast = q.isFastTrack;
            return (
              <li key={q.id ?? i} className="flex items-center justify-between text-sm">
                <span className="text-[color:var(--text-secondary)] truncate pr-2">
                  {name}
                  {isFast && <span className="ml-1 text-[9px] font-bold uppercase tracking-wider text-[color:var(--jarvis)]">Fast</span>}
                </span>
                <span className={`font-bold tabular-nums shrink-0 ${wait > 20 ? "text-[color:var(--danger)]" : wait > 10 ? "text-amber-600" : "text-[color:var(--success)]"}`}>
                  {wait != null ? `${wait} min` : "—"}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function FlightTimeline({ status, watch, landed }) {
  const now = useTickingNow();
  const [mapOpen, setMapOpen] = useState(false);

  // För arrival: start = origin (utlandet), slut = airport_iata (Sverige)
  // För departure: start = airport_iata (Sverige), slut = destination (utlandet)
  const isArrival = watch.kind === "arrival";
  const startIata = isArrival ? (status.origin || status.destination) : watch.airport_iata;
  const endIata = isArrival ? watch.airport_iata : status.destination;

  // Faktisk dep/arr-tid → räkna duration exakt om vi har båda. Prioritera
  // actual_takeoff_utc/actual_landing_utc (ADS-B-derived, mest exakta) FÖRE
  // Swedavia/ADB-tider. För departures: Swedavia sätter ofta actual_time
  // till landings-tid (när planet landade på destinationen) — använd alltså
  // INTE actual_time som depIso för departures.
  const depIsoForCalc = isArrival
    ? (status.departure_actual || status.departure_estimated || status.departure_scheduled)
    : (status.actual_takeoff_utc
        || status.departure_actual || status.departure_estimated
        || status.departure_scheduled || status.scheduled_time);
  const arrIsoForCalc = isArrival
    ? (status.actual_landing_utc
        || status.actual_time || status.estimated_time || status.scheduled_time)
    : (status.actual_landing_utc
        || status.arrival_actual || status.arrival_estimated
        || status.arrival_scheduled);

  let pct = 0;
  let labelMid = "";

  // STICKY take-off-detektor: har planet någonsin lyft? Vi får INTE förlita
  // oss på enbart nuvarande `live_position.altitude_m >= 300` — ADS-B-data
  // försvinner ofta vid final approach, mid-flight signal-glapp eller pga
  // adsb.lol-rate-limit, och då vill vi INTE resetta timeline till
  // "Inväntar avgång". Använd "ALLT vi vet"-signal:
  //   - actual_takeoff_utc satt (wheels-up bekräftat tidigare)
  //   - aero_status_text i {EnRoute, Departed, Landed, Arrived, Diverted}
  //   - Swedavia-status i {ACT, FLT, LAN, ARR}
  //   - position_history har minst en sample med altitude_m >= 300
  //   - just nu är live_position.altitude_m >= 300 (kanske nyligen)
  const _livePos = status.live_position || {};
  const _altM = Number(_livePos.altitude_m || _livePos.altitude || 0);
  const _onGround = _livePos.on_ground === true;
  const _aeroStatusNorm = String(status.aero_status_text || "").toLowerCase().replace(/[\s_-]/g, "");
  const _swStatus = String(status.status || "").toUpperCase();
  const _historyAlt = (status.position_history || []).some(
    (p) => Number(p?.altitude_m || p?.altitude || 0) >= 300,
  );
  const hasEverLifted =
    !!status.actual_takeoff_utc
    || ["enroute", "departed", "landed", "arrived", "diverted"].includes(_aeroStatusNorm)
    || ["ACT", "FLT", "LAN", "ARR", "DEP"].includes(_swStatus)
    || _historyAlt
    || (_altM >= 300 && !_onGround);

  try {
    // Försök räkna real duration från depIso → arrIso. Fallback till tabell.
    let estDurationMin = estimateDurationMinutes(startIata, endIata);
    const depMs = depIsoForCalc ? new Date(_normalizeIso(depIsoForCalc)).getTime() : NaN;
    const arrMs = arrIsoForCalc ? new Date(_normalizeIso(arrIsoForCalc)).getTime() : NaN;
    if (!isNaN(depMs) && !isNaN(arrMs) && arrMs > depMs) {
      estDurationMin = (arrMs - depMs) / 60_000;
    }
    if (isArrival) {
      // För arrival: håll planet vid start tills vi har bekräftelse på
      // att det HAR LYFT från den utländska avgångsflygplatsen. STICKY
      // — när hasEverLifted blir true återgår timeline ALDRIG till
      // "Inväntar avgång", även om ADS-B-data tappas tillfälligt vid
      // final approach.
      // Använd actual_landing_utc (ADS-B touchdown) om finns, annars
      // estimated/scheduled. Aldrig fall tillbaka på actual_time eftersom
      // det blir samma som scheduled_time för ej-landade flighter.
      const arr = new Date(_normalizeIso(
        status.actual_landing_utc || status.estimated_time || status.scheduled_time
      )).getTime();
      if (!hasEverLifted && !landed) {
        pct = 0;
        labelMid = "Inväntar avgång";
      } else if (!isNaN(arr)) {
        const remainingMin = (arr - now) / 60_000;
        const elapsedMin = Math.max(0, estDurationMin - remainingMin);
        pct = Math.max(0, Math.min(100, (elapsedMin / estDurationMin) * 100));
        if (landed) {
          pct = 100;
          labelMid = "Landad";
        } else if (remainingMin < 1) {
          labelMid = "Landar snart…";
        } else if (remainingMin < 60) {
          labelMid = `~${Math.round(remainingMin)} min kvar`;
        } else {
          const h = Math.floor(remainingMin / 60);
          const m = Math.round(remainingMin % 60);
          labelMid = `~${h}h ${m}min kvar`;
        }
      }
    } else {
      // Departure: animeringen startar INTE förrän vi har sticky-bekräftelse
      // på att planet HAR LYFT. När hasEverLifted blir true återgår
      // timeline ALDRIG till "På marken" även om ADS-B tappas tillfälligt.
      // Använd actual_takeoff_utc (ADS-B wheels-up) om finns — annars
      // faller vi tillbaka på estimated/scheduled. Aldrig actual_time
      // för departures (kan vara landings-tid).
      const dep = new Date(_normalizeIso(
        status.actual_takeoff_utc || status.estimated_time || status.scheduled_time
      )).getTime();
      if (!hasEverLifted && !landed) {
        // På marken: håll flygplanet vid start
        pct = 0;
        labelMid = "På marken";
      } else if (!isNaN(dep)) {
        const elapsed = (now - dep) / 60_000;
        pct = Math.max(0, Math.min(100, (elapsed / estDurationMin) * 100));
        if (landed) pct = 100;
        const remaining = Math.max(0, estDurationMin - elapsed);
        if (landed) {
          labelMid = "Landad";
        } else if (remaining < 1) {
          labelMid = "Landar snart…";
        } else if (remaining < 60) {
          labelMid = `~${Math.round(remaining)} min kvar`;
        } else {
          const h = Math.floor(remaining / 60);
          const m = Math.round(remaining % 60);
          labelMid = `~${h}h ${m}min kvar`;
        }
      }
    }
  } catch { /* ignore */ }

  return (
    <div className="card-soft p-3" data-testid="flight-timeline">
      <div className="flex items-center justify-between mb-3">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-[color:var(--text-tertiary)]">
          Resans gång
        </p>
        <button
          onClick={() => setMapOpen(true)}
          className="text-[10px] font-medium px-2 py-0.5 rounded bg-[color:var(--jarvis)] text-white hover:opacity-90 transition flex items-center gap-1"
          data-testid="flight-open-map-btn"
        >
          <MapIcon size={11} />
          Öppna karta
        </button>
      </div>
      <div className="relative h-10">
        <div className="absolute top-1/2 left-0 right-0 h-0.5 bg-[color:var(--border-subtle)] rounded -translate-y-1/2" />
        <div
          className="absolute top-1/2 left-0 h-0.5 rounded -translate-y-1/2 transition-all duration-1000"
          style={{ width: `${pct}%`, background: "var(--jarvis)" }}
        />
        {/* Start-flagga */}
        <div
          className="absolute top-1/2 -translate-y-1/2 left-0 -translate-x-1/2 text-base leading-none select-none"
          title={startIata}
          data-testid="flight-timeline-start-flag"
        >
          {flagForIata(startIata)}
        </div>
        {/* Stopp-flagga */}
        <div
          className="absolute top-1/2 -translate-y-1/2 right-0 translate-x-1/2 text-base leading-none select-none"
          title={endIata || ""}
          data-testid="flight-timeline-end-flag"
        >
          {flagForIata(endIata)}
        </div>
        {/* Flygplan — roterat 45° så det pekar mot målet (höger). */}
        <div
          className="absolute top-1/2 -translate-y-1/2 transition-all duration-1000"
          style={{ left: `${pct}%`, transform: `translate(-50%, -50%) rotate(45deg)` }}
        >
          <Plane size={18} className="text-[color:var(--jarvis)] drop-shadow" fill="currentColor" />
        </div>
      </div>
      <div className="flex items-center justify-between mt-2 text-[10px] text-[color:var(--text-tertiary)] tabular-nums">
        <span>{startIata || "?"}</span>
        <span className="font-medium text-[color:var(--text-secondary)]">{labelMid}</span>
        <span>{endIata || "?"}</span>
      </div>
      <FlightMapModal open={mapOpen} onClose={() => setMapOpen(false)} watch={watch} />
    </div>
  );
}

// IATA-flygplats → ISO-2-landskod → emoji-flagga
const IATA_TO_CC = {
  // Sverige
  ARN: "SE", GOT: "SE", MMX: "SE", BMA: "SE", LLA: "SE", UME: "SE",
  OSD: "SE", VBY: "SE", KRN: "SE", RNB: "SE", AGH: "SE", JKG: "SE",
  KSD: "SE", NYO: "SE", VST: "SE", VXO: "SE",
  // Norden
  OSL: "NO", BGO: "NO", TRD: "NO", SVG: "NO", TOS: "NO", BOO: "NO", TRF: "NO",
  CPH: "DK", AAL: "DK", BLL: "DK", AAR: "DK",
  HEL: "FI", TKU: "FI", OUL: "FI", TMP: "FI",
  KEF: "IS", REK: "IS",
  // Spanien
  MAD: "ES", BCN: "ES", AGP: "ES", PMI: "ES", ALC: "ES", IBZ: "ES",
  TFS: "ES", TFN: "ES", LPA: "ES", VLC: "ES", SVQ: "ES", BIO: "ES",
  FUE: "ES", ACE: "ES", GRX: "ES", REU: "ES", MJV: "ES",
  // Portugal
  LIS: "PT", OPO: "PT", FAO: "PT", FNC: "PT", PDL: "PT",
  // Frankrike
  CDG: "FR", ORY: "FR", NCE: "FR", LYS: "FR", MRS: "FR", TLS: "FR",
  BOD: "FR", NTE: "FR",
  // Tyskland
  FRA: "DE", MUC: "DE", BER: "DE", DUS: "DE", HAM: "DE", STR: "DE", CGN: "DE",
  // Italien
  FCO: "IT", MXP: "IT", LIN: "IT", VCE: "IT", BLQ: "IT", NAP: "IT",
  CTA: "IT", PMO: "IT", BRI: "IT",
  // UK & Irland
  LHR: "GB", LGW: "GB", LCY: "GB", STN: "GB", LTN: "GB", MAN: "GB",
  EDI: "GB", BHX: "GB", BRS: "GB", GLA: "GB", ABZ: "GB", INV: "GB",
  DUB: "IE", ORK: "IE", SNN: "IE",
  // Övriga Europa
  AMS: "NL", EIN: "NL", RTM: "NL",
  BRU: "BE", CRL: "BE", ANR: "BE",
  ZRH: "CH", GVA: "CH", BSL: "CH",
  VIE: "AT", SZG: "AT", INN: "AT",
  PRG: "CZ", BRQ: "CZ",
  BUD: "HU",
  WAW: "PL", WMI: "PL", KRK: "PL", GDN: "PL", POZ: "PL", WRO: "PL",
  TLL: "EE", RIX: "LV", VNO: "LT", KUN: "LT",
  ATH: "GR", SKG: "GR", HER: "GR", RHO: "GR", CFU: "GR", CHQ: "GR",
  KGS: "GR", JTR: "GR",
  ZAG: "HR", SPU: "HR", DBV: "HR", PUY: "HR", RJK: "HR", ZAD: "HR",
  IST: "TR", SAW: "TR", AYT: "TR", BJV: "TR", ADB: "TR",
  // Världen
  DXB: "AE", DWC: "AE", AUH: "AE",
  DOH: "QA",
  BKK: "TH", DMK: "TH", HKT: "TH", USM: "TH", CNX: "TH",
  NRT: "JP", HND: "JP", KIX: "JP",
  PEK: "CN", PVG: "CN", PKX: "CN",
  HKG: "HK", SIN: "SG",
  JFK: "US", EWR: "US", LAX: "US", ORD: "US", MIA: "US", BOS: "US",
  IAD: "US", ATL: "US", DFW: "US", SFO: "US",
};

function ccToFlag(cc) {
  if (!cc || cc.length !== 2) return "";
  const codePoints = cc.toUpperCase().split("").map((c) => 127397 + c.charCodeAt(0));
  return String.fromCodePoint(...codePoints);
}

function flagForIata(iata) {
  const cc = IATA_TO_CC[(iata || "").toUpperCase()];
  return cc ? ccToFlag(cc) : "🏳️";
}

export function _normalizeIso(iso) {
  // AeroDataBox returnerar "2026-06-19 14:45Z" (mellanslag) som Safari INTE
  // tolkar som UTC. Byt mellanslag → "T" så vi får strikt ISO 8601.
  return typeof iso === "string" ? iso.replace(" ", "T") : iso;
}

function formatTime(iso) {
  if (!iso) return "";
  try {
    return new Date(_normalizeIso(iso)).toLocaleTimeString("sv-SE", {
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return ""; }
}

function estimateDurationMinutes(from, to) {
  const t = (to || "").toUpperCase();
  if (["CPH", "OSL", "GOT", "MMX", "BMA"].includes(t)) return 70;
  if (["HEL", "TLL", "RIX"].includes(t)) return 75;
  if (["AMS", "BRU", "BER", "HAM", "FRA"].includes(t)) return 150;
  if (["LHR", "LGW", "STN", "MAN", "EDI", "DUB"].includes(t)) return 165;
  if (["CDG", "ORY", "ZRH", "MUC", "VIE", "WAW", "PRG"].includes(t)) return 175;
  if (["BCN", "MAD", "FCO", "MXP", "ATH"].includes(t)) return 200;
  if (["PMI", "AGP", "ALC", "VLC", "IBZ", "TFS", "LPA"].includes(t)) return 230;
  if (["DXB", "IST"].includes(t)) return 360;
  if (["JFK", "EWR", "BOS"].includes(t)) return 480;
  if (["BKK", "LAX", "NRT"].includes(t)) return 660;
  return 150;
}
